import React, { useEffect, useMemo, useState } from 'react';
import './FolderImageLabelModal.css';

const PAGE_SIZE = 10;

export interface FolderImageLabelModalProps {
  open: boolean;
  files: File[];
  areaKeys: string[];
  getAreaDisplayName: (area: string) => string;
  onClose: () => void;
  /** Group files by area and append to collections */
  onApply: (byArea: Record<string, File[]>) => void;
}

const FolderImageLabelModal: React.FC<FolderImageLabelModalProps> = ({
  open,
  files,
  areaKeys,
  getAreaDisplayName,
  onClose,
  onApply,
}) => {
  const defaultArea = areaKeys[0] ?? 'exterior';
  const [page, setPage] = useState(0);
  const [areaByIndex, setAreaByIndex] = useState<Record<number, string>>({});
  const [thumbUrls, setThumbUrls] = useState<Record<number, string>>({});

  const totalPages = Math.max(1, Math.ceil(files.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages - 1);
  const pageStart = safePage * PAGE_SIZE;
  const pageFiles = files.slice(pageStart, pageStart + PAGE_SIZE);

  useEffect(() => {
    if (!open) return;

    setPage(0);
    const init: Record<number, string> = {};
    files.forEach((_, i) => {
      init[i] = defaultArea;
    });
    setAreaByIndex(init);
  }, [open, files, defaultArea]);

  useEffect(() => {
    if (!open || files.length === 0) return;

    const next: Record<number, string> = {};
    for (let i = pageStart; i < pageStart + pageFiles.length; i++) {
      const file = files[i];
      if (file) next[i] = URL.createObjectURL(file);
    }

    setThumbUrls((prev) => {
      Object.values(prev).forEach((u) => URL.revokeObjectURL(u));
      return next;
    });

    return () => {
      Object.values(next).forEach((u) => URL.revokeObjectURL(u));
    };
  }, [open, safePage, pageStart, pageFiles.length, files]);

  useEffect(() => {
    if (open) return;
    setThumbUrls((prev) => {
      Object.values(prev).forEach((u) => URL.revokeObjectURL(u));
      return {};
    });
  }, [open]);

  const setAreaForGlobalIndex = (globalIndex: number, area: string) => {
    setAreaByIndex((prev) => ({ ...prev, [globalIndex]: area }));
  };

  const validAreaSet = useMemo(() => new Set(areaKeys), [areaKeys]);

  const handleApply = () => {
    const byArea: Record<string, File[]> = {};
    const fallback = areaKeys[0] ?? 'exterior';

    files.forEach((file, i) => {
      let area = areaByIndex[i] ?? fallback;
      if (!validAreaSet.has(area)) area = fallback;
      if (!byArea[area]) byArea[area] = [];
      byArea[area].push(file);
    });

    onApply(byArea);
    onClose();
  };

  if (!open) return null;

  return (
    <div className="modal-overlay folder-image-label-overlay" onClick={onClose}>
      <div
        className="modal-content folder-image-label-modal"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="folder-image-label-title"
      >
        <div className="modal-header">
          <h3 id="folder-image-label-title">Label imported images</h3>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">
            <i className="fas fa-times" aria-hidden />
          </button>
        </div>

        <div className="modal-body folder-image-label-body">
          <p className="folder-image-label-intro">
            Choose the area for each photo (page {safePage + 1} of {totalPages}, {files.length} total).
          </p>

          <div className="folder-image-label-table-wrap">
            <table className="folder-image-label-table">
              <thead>
                <tr>
                  <th scope="col">Preview</th>
                  <th scope="col">File</th>
                  <th scope="col">Area</th>
                </tr>
              </thead>
              <tbody>
                {pageFiles.map((file, j) => {
                  const globalIndex = pageStart + j;
                  const area = areaByIndex[globalIndex] ?? defaultArea;
                  const thumb = thumbUrls[globalIndex];
                  return (
                    <tr key={`${file.name}-${globalIndex}`}>
                      <td className="folder-image-label-thumb-cell">
                        {thumb ? (
                          <img src={thumb} alt="" className="folder-image-label-thumb" />
                        ) : (
                          <div className="folder-image-label-thumb-placeholder" />
                        )}
                      </td>
                      <td className="folder-image-label-name-cell">
                        <span className="folder-image-label-name" title={file.name}>
                          {file.name}
                        </span>
                      </td>
                      <td>
                        <select
                          className="form-input folder-image-label-select"
                          value={area}
                          onChange={(e) => setAreaForGlobalIndex(globalIndex, e.target.value)}
                          aria-label={`Area for ${file.name}`}
                        >
                          {areaKeys.map((key) => (
                            <option key={key} value={key}>
                              {getAreaDisplayName(key)}
                            </option>
                          ))}
                        </select>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="folder-image-label-pagination">
            <button
              type="button"
              className="btn-secondary folder-image-label-page-btn"
              disabled={safePage <= 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
            >
              <i className="fas fa-chevron-left" aria-hidden />
              Previous
            </button>
            <span className="folder-image-label-page-indicator">
              Page {safePage + 1} / {totalPages}
            </span>
            <button
              type="button"
              className="btn-secondary folder-image-label-page-btn"
              disabled={safePage >= totalPages - 1}
              onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
            >
              Next
              <i className="fas fa-chevron-right" aria-hidden />
            </button>
          </div>
        </div>

        <div className="folder-image-label-footer">
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button type="button" className="btn-primary" onClick={handleApply}>
            <i className="fas fa-check" aria-hidden />
            Apply to library
          </button>
        </div>
      </div>
    </div>
  );
};

export default FolderImageLabelModal;
